import {
  useEffect,
  useId,
  useRef,
  type PropsWithChildren,
  type ReactNode,
} from "react";

let lastActivationTarget: HTMLElement | null = null;
if (typeof document !== "undefined") {
  document.addEventListener("focusin",(event)=>{if(event.target instanceof HTMLElement&&!event.target.closest('[role="dialog"]')){lastActivationTarget=event.target.closest<HTMLElement>('button,a[href],[role="button"]')??event.target;}},true);
  document.addEventListener(
    "click",
    (event) => {
      if (
        event.target instanceof HTMLElement &&
        !event.target.closest('[role="dialog"]')
      )
        lastActivationTarget =
          event.target.closest<HTMLElement>('button,a[href],[role="button"]') ??
          event.target;
    },
    true,
  );
}

export function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <img
      className={compact ? "brand-logo brand-logo--compact" : "brand-logo"}
      src="/brand/hardnup-logo.png"
      alt="Hard'n'Up"
    />
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <span className="spinner" aria-hidden="true" />
      {label}
    </div>
  );
}

export function ErrorNotice({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss?: () => void;
}) {
  return (
    <div className="notice notice--error" role="alert">
      <span>{message}</span>
      {onDismiss && (
        <button
          className="icon-button"
          onClick={onDismiss}
          aria-label="Dismiss error"
        >
          ×
        </button>
      )}
    </div>
  );
}

export function SuccessNotice({ children }: PropsWithChildren) {
  return (
    <div className="notice notice--success" role="status">
      {children}
    </div>
  );
}

export function Card({
  children,
  className = "",
  labelledBy,
}: PropsWithChildren<{ className?: string; labelledBy?: string }>) {
  return (
    <section className={`card ${className}`} aria-labelledby={labelledBy}>
      {children}
    </section>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

export function Empty({ title, body }: { title: string; body: string }) {
  return (
    <Card className="empty">
      <h2>{title}</h2>
      <p>{body}</p>
    </Card>
  );
}

export function formatDuration(seconds: number | null | undefined) {
  const value = Math.max(0, seconds ?? 0);
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  return hours ? `${hours} hr ${minutes} min` : `${minutes} min`;
}

export function formatDate(value: string | Date | null | undefined) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

export function Modal({
  title,
  onClose,
  children,
}: PropsWithChildren<{ title: string; onClose: () => void }>) {
  const active =
    document.activeElement instanceof HTMLElement &&
    document.activeElement !== document.body
      ? document.activeElement
      : null;
  const dialog = useRef<HTMLElement>(null);
  const opener = useRef<HTMLElement | null>(lastActivationTarget ?? active);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const titleId = useId();
  const returnFocus=()=>{const target=opener.current;requestAnimationFrame(()=>requestAnimationFrame(()=>{if(target?.isConnected && !document.querySelector('[role="dialog"]'))target.focus();}));};
  const requestClose=()=>{closeRef.current();returnFocus();};
  useEffect(() => {
    const element = dialog.current;
    const focusable = () => [
      ...(element?.querySelectorAll<HTMLElement>(
        'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      ) ?? []),
    ];
    focusable()[0]?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        requestClose();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0],
        last = items.at(-1)!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.removeEventListener("keydown", keydown);
      returnFocus();
    };
  }, []);
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) requestClose();
      }}
    >
      <section
        ref={dialog}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header>
          <h2 id={titleId}>{title}</h2>
          <button className="icon-button" onClick={requestClose} aria-label="Close">
            ×
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
